/**
 * TransportBar — the shared transport icon-button row used by every editor.
 *
 * Config-driven: the PRESETS registry below holds the SUPERSET of every editor's
 * transport action (icon + tooltip defined once). Each editor passes only the
 * actions it supports, in its own order, as `{ id, active, onClick }` — so the
 * common ones are unified and the rest are simply turned on per editor.
 *
 * The visual (button look + grid + tooltips) is identical everywhere; the state
 * and handlers come from each editor's store.
 */

import React from 'react';
import { Tooltip, tip } from './Tooltip';
import { color, font } from './theme';
import {
  IcSettings, IcGrid, IcMetro, IcWaveBars, IcFigure, IcSpectrum, IcStats, IcLibrary, IcBug, IcWarn, IcTimeline3D, IcMonitor, IcMirror,
} from './icons';

type IconC = React.ComponentType<{ size?: number; sw?: number }>;

interface Preset { Icon?: IconC; title: string; body: string; shortcut?: string; note?: string }

/** Superset of every editor's transport action — the single source of icon +
 *  tooltip copy. (`record` has no Icon: it renders the custom blinking dot.) */
const PRESETS: Record<string, Preset> = {
  settings:   { Icon: IcSettings, title: 'Editor Settings', body: 'Open the editor settings panel — theme, gameplay and interface options.' },
  grid:       { Icon: IcGrid, title: 'Snap Grid', body: 'Overlay lane dividers on the highway so you can see exactly where each note slot sits.' },
  beatgrid:   { Icon: IcTimeline3D, title: '3D Track', body: 'The whole 3D highway — floor, edges, beat/measure grid, snap & trigger lines. Turn it off for a clean stage: just your notes & decor floating in space.' },
  metronome:  { Icon: IcMetro, title: 'Metronome', body: 'Hear a click on every beat during playback — great for placing notes by ear.' },
  waveform:   { Icon: IcWaveBars, title: 'Timeline Waveform', body: 'Show the audio shape on the timeline so you can locate drops, breaks and build-ups at a glance.' },
  mannequin:  { Icon: IcFigure, title: 'Player Mannequin', body: 'Place a body-scale mannequin in the 3D view to verify notes are comfortably reachable.' },
  heatmap:    { Icon: IcSpectrum, title: 'Intensity Heatmap', body: 'Colour the highway by note density — spot packed sections vs. breathing room.' },
  difficulty: { Icon: IcStats, title: 'Difficulty Meter', body: "Live gauge of your map's note density. Keep it inside the tier that matches the intended skill level." },
  render:     { Icon: IcMonitor, title: 'Render & Draw Distance', body: 'Live scene stats (FPS, draw calls, triangles) plus the draw-distance (FOV) controls — cap how far ahead notes and obstacles render to keep dense maps smooth. Default 12 beats.', shortcut: '`' },
  mirror:     { Icon: IcMirror, title: 'Mirror Mode', body: 'When on, every note or wall you place also drops its mirror on the opposite hand (X-mirrored). Press Y to also flip vertically.', shortcut: 'F3' },
  library:    { Icon: IcLibrary, title: 'Pattern Library', body: 'Browse and stamp saved note patterns to reuse a sequence anywhere in the map.', shortcut: 'Ctrl+L' },
  record:     { title: 'Live Record Mode', body: 'Tap keys while the track plays to stamp notes in real time.', note: 'Short tap → single note · Hold key → trail' },
  debug:      { Icon: IcBug, title: 'Sync Debug Console', body: 'Stream audio-sync drift data — use it if notes feel slightly off-beat during playback.', shortcut: 'Ctrl+Shift+D' },
  warn:       { Icon: IcWarn, title: 'Complexity Overlay', body: 'Highlight overly complex sections so you can spot and smooth out sudden difficulty spikes.' },
};

export interface TransportItem {
  id: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  /** Custom pre-rendered button icon (rarely needed — registry/record cover it). */
  icon?: React.ReactNode;
  /** Per-item overrides for the registry copy. */
  Icon?: IconC;
  title?: string;
  body?: string;
  shortcut?: string;
  note?: string;
}

/** The red record dot — blinks while recording. */
function RecordDot({ active }: { active: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ display: 'block' }} aria-hidden="true">
      <circle cx="12" cy="12" r="9" fill="none" stroke="#EF4444" strokeWidth="2" />
      <circle cx="12" cy="12" r="4.5" fill="#EF4444" className={active ? 'rec-blink' : undefined} />
    </svg>
  );
}

export default function TransportBar({ items }: { items: TransportItem[] }) {
  return (
    <div style={rowStyle}>
      {items.map(it => {
        const p = PRESETS[it.id] ?? { title: it.id, body: '' };
        const Icon = it.Icon ?? p.Icon;
        const title = it.title ?? p.title;
        const body = it.body ?? p.body;
        const shortcut = it.shortcut ?? p.shortcut;
        const note = it.note ?? p.note;
        const btnIcon = it.icon
          ?? (it.id === 'record' ? <RecordDot active={!!it.active} /> : Icon ? <Icon size={16} /> : null);
        return (
          <Tooltip key={it.id} content={tip({ title, icon: Icon ? <Icon size={13} /> : undefined, body, shortcut, note })}>
            <button
              onClick={(e) => { (e.currentTarget as HTMLButtonElement).blur(); it.onClick(); }}
              disabled={it.disabled}
              style={{ ...btnStyle, ...activeStyle(!!it.active), opacity: it.disabled ? 0.5 : 1 }}
            >
              {btnIcon}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 6,
};

const btnStyle: React.CSSProperties = {
  width: '100%', maxWidth: 36, minWidth: 0, height: 32,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  margin: '0 auto', border: '1px solid', borderRadius: 8,
  cursor: 'pointer', padding: 0, fontFamily: font.sans,
  transition: 'all 0.15s ease', boxSizing: 'border-box',
};

function activeStyle(active: boolean): React.CSSProperties {
  return {
    background: active
      ? 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.25), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.15))'
      : 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
    borderColor: active
      ? 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)'
      : 'oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.12)',
    color: active ? color.accent : color.inkFaint,
  };
}
